import { useState, type ReactNode } from 'react'
import { cx } from '../../ui/classes'
import { Alert, ChevronDown } from '../../ui/icons'
import {
  countActionableProblems,
  problemsTone,
  type PosProblem,
  type ProblemSeverity
} from '../services/shift/posProblems'

const TONE_CLASS = {
  danger: 'border-warnLine bg-red-50 text-dangerDark',
  warn: 'border-amber-200 bg-amber-50 text-amber-900',
  neutral: 'border-line bg-surface text-muted2'
} as const

const BADGE_CLASS = {
  danger: 'border-warnLine bg-white text-dangerDark',
  warn: 'border-amber-300 bg-white text-amber-900',
  neutral: 'border-line bg-white text-muted2'
} as const

export interface PosProblemAction {
  label: string
  onClick: () => void
  disabled?: boolean
}

interface PosProblemsPanelProps {
  problems: PosProblem[]
  /** Раскрыт ли блок. По умолчанию свёрнут всегда. */
  open: boolean
  onToggle: () => void
  /** Пометить info-уведомление прочитанным. */
  onAcknowledge?: (id: PosProblem['id']) => void
  /** Прочитанные уведомления: остаются в списке, но неактивны. */
  acknowledged?: PosProblem['id'][]
  /** Краткая подсветка при появлении критичной проблемы. */
  highlighted?: boolean
  severity: ProblemSeverity | null
  /** Действие внутри проблемы; `null` — только текст. */
  actions?: Partial<
    Record<PosProblem['id'], { main?: PosProblemAction; more?: PosProblemAction[] }>
  >
  /** Детали под строкой (например, список замен или hold-чеков). */
  details?: Partial<Record<PosProblem['id'], ReactNode>>
  compact?: boolean
  className?: string
}

/**
 * Единая свёрнутая строка «Уведомления» вместо отдельных плашек.
 *
 * Цвет отражает только тяжесть: красный — критичная, amber — предупреждение,
 * нейтральный — информационные. Содержимое рендерится лениво, чтобы свёрнутый
 * блок не держал в DOM списки чеков.
 */
export function PosProblemsPanel({
  problems,
  open,
  onToggle,
  onAcknowledge,
  acknowledged = [],
  highlighted = false,
  severity,
  actions = {},
  details = {},
  compact = false,
  className
}: PosProblemsPanelProps) {
  const tone = problemsTone(severity)
  const total = countActionableProblems(problems)
  const criticalCount = problems.filter((p) => p.severity === 'critical').length
  // Строка «Уведомления» видна всегда: пустое состояние — тоже информация
  // (кассир не ищет блок, который то появляется, то исчезает).
  const empty = problems.length === 0

  return (
    <div
      className={cx(
        'mb-2 rounded-card border',
        TONE_CLASS[tone],
        highlighted && 'problems-highlight',
        className
      )}
      data-testid="pos-problems"
      data-tone={tone}
    >
      <button
        type="button"
        className={cx(
          'flex w-full cursor-pointer items-center gap-2 bg-transparent px-3 text-left font-semibold text-inherit',
          compact ? 'min-h-11' : 'py-2'
        )}
        onClick={onToggle}
        aria-expanded={open}
        aria-controls="pos-problems-body"
        data-testid="pos-problems-toggle"
      >
        <Alert size={18} className="shrink-0" />
        <span className="whitespace-nowrap">Уведомления</span>
        {empty ? (
          <span className="text-tiny font-normal opacity-70">нет</span>
        ) : (
          total > 0 && (
            <span
              className={cx(
                'inline-flex min-w-6 items-center justify-center rounded-full border px-1.5 py-0.5 text-tiny',
                BADGE_CLASS[tone]
              )}
            >
              {total}
            </span>
          )
        )}
        {criticalCount > 0 && (
          <span className="text-tiny font-normal opacity-80">
            критичных: {criticalCount}
          </span>
        )}
        {!empty && (
          <ChevronDown
            size={18}
            className={cx('ml-auto shrink-0 transition-transform', open && 'rotate-180')}
          />
        )}
      </button>

      {open && !empty && (
        <div id="pos-problems-body" className="space-y-1.5 px-3 pb-2" data-testid="pos-problems-body">
          {problems.map((problem) => (
            <ProblemRow
              key={problem.id}
              problem={problem}
              action={actions[problem.id]?.main}
              more={actions[problem.id]?.more}
              details={details[problem.id]}
              onAcknowledge={onAcknowledge}
              acknowledged={problem.severity === 'info' && acknowledged.includes(problem.id)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

interface ProblemRowProps {
  problem: PosProblem
  action?: PosProblemAction
  more?: PosProblemAction[]
  details?: ReactNode
  onAcknowledge?: (id: PosProblem['id']) => void
  /** Прочитанное info-уведомление: показываем приглушённо, без действия. */
  acknowledged?: boolean
}

function ProblemRow({
  problem,
  action,
  more,
  details,
  onAcknowledge,
  acknowledged = false
}: ProblemRowProps) {
  const [openMore, setOpenMore] = useState(false)
  return (
    <div
      className={cx(
        'rounded-card border border-current/20 bg-white/70 px-2.5 py-1.5',
        acknowledged && 'opacity-60'
      )}
      data-testid={`pos-problem-${problem.id}`}
      data-severity={problem.severity}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="min-w-0 flex-1 text-small font-semibold">
          {problem.title}
          {problem.count != null && (
            <span className="ml-1 font-bold tabular-nums">: {problem.count}</span>
          )}
        </span>
        {action && (
          <button
            type="button"
            className="min-h-9 cursor-pointer rounded-md border border-current bg-white px-2.5 py-1 text-small font-semibold text-inherit disabled:cursor-not-allowed disabled:opacity-50"
            onClick={action.onClick}
            disabled={action.disabled}
          >
            {action.label}
          </button>
        )}
        {problem.severity === 'info' &&
          onAcknowledge &&
          (acknowledged ? (
            <span className="text-tiny font-normal opacity-80">прочитано</span>
          ) : (
            <button
              type="button"
              className="min-h-9 cursor-pointer rounded-md border border-current bg-white px-2.5 py-1 text-small font-semibold text-inherit"
              onClick={() => onAcknowledge(problem.id)}
              aria-label={`Понятно: ${problem.title}`}
            >
              Понятно
            </button>
          ))}
        {more && more.length > 0 && (
          <button
            type="button"
            className="min-h-9 min-w-9 cursor-pointer rounded-md border border-current bg-white px-2 py-1 text-small font-semibold text-inherit"
            onClick={() => setOpenMore((v) => !v)}
            aria-expanded={openMore}
            aria-label={`Другие действия: ${problem.title}`}
          >
            …
          </button>
        )}
      </div>
      {openMore && more && more.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid={`pos-problem-${problem.id}-more`}>
          {more.map((item) => (
            <button
              key={item.label}
              type="button"
              className="min-h-9 cursor-pointer rounded-md border border-current bg-white px-2.5 py-1 text-small font-semibold text-inherit disabled:cursor-not-allowed disabled:opacity-50"
              onClick={item.onClick}
              disabled={item.disabled}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
      {details && <div className="mt-1.5">{details}</div>}
    </div>
  )
}
